import React, { useEffect, useState } from "react";
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { toProtectedFileUrl } from '../../utils/secureFile';
import { Modal, ModalHeader, ModalBody, Row, Col, Alert, Spinner, Button } from "reactstrap";
import moment from "moment";
import axios from "../../api";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

const RowItem = ({ label, value }) => (
  <div className="mb-2">
    <strong>{label}</strong>
    <div>{value || '-'}</div>
  </div>
);

const ViewModal = ({ isOpen, toggle, item }) => {
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isMobile = () =>
    typeof window !== 'undefined' &&
    (/Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent) || window.innerWidth <= 800);

  useEffect(() => {
    let objectUrl = "";
    const fetchPdf = async () => {
      if (!isOpen || !item?.id) {
        setPdfUrl("");
        return;
      }
      if (!item.file) {
        setPdfUrl("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`/downloadpdfregisterusulan/${item.id}/download`, { responseType: 'blob' });
        objectUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPdfUrl(objectUrl);
      } catch (e) {
        setError("Gagal memuat PDF");
        setPdfUrl("");
      } finally {
        setLoading(false);
      }
    };

    fetchPdf();
    return () => {
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [isOpen, item]);

  // Fungsi untuk membuka PDF di tab baru
  const openPdfInNewTab = () => {
    if (pdfUrl) {
      openInAppFilePreview({ url: pdfUrl });
    }
  };

  // Fungsi untuk download PDF
  const downloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = item.file || `register-usulan-${item.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Detail Register Usulan</ModalHeader>
      <ModalBody>
        {item ? (
          <Row>
            <Col md={6}>
              <RowItem label="Tanggal Terima" value={item.tanggalDiterima ? moment(item.tanggalDiterima).format('YYYY-MM-DD') : ''} />
              <RowItem label="Nomor Surat" value={item.nomorSurat} />
              <RowItem label="Tanggal Surat" value={item.tanggalSurat ? moment(item.tanggalSurat).format('YYYY-MM-DD') : ''} />
              <RowItem label="Nama Pemohon / Lembaga Pengusul" value={item.namaPemohonLembagaPengusul} />
              <RowItem label="Nama Kepala Keluarga" value={item.namaKepalaKeluarga} />
              <RowItem label="Nomor KK" value={item.nomorKK} />
              <RowItem label="NIK" value={item.nik} />
              <RowItem label="Alamat Pemohon" value={item.alamatPemohon} />
              <RowItem label="Kecamatan" value={item.kecamatan} />
              <RowItem label="Desa / Kelurahan" value={item.desaKelurahan} />
              <RowItem label="Perihal / Usulan" value={item.perihal} />
            </Col>
            <Col md={6}>
              <RowItem label="Disposisi Dari" value={item.disposisiDari} />
              <RowItem label="Diteruskan Kepada" value={item.diteruskanKepada} />
              <RowItem label="Catatan / Tindak Lanjut" value={item.catatan} />
              <RowItem label="Status" value={item.status} />
              <RowItem label="Di Input oleh" value={item.Admin.username} />
              
              {item.file && (
                <div className="mt-2">
                  {/* Header untuk PDF dengan tombol aksi */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Lampiran PDF:</strong>
                    <div className="d-flex gap-2">
                      {/* Tombol Preview (canvas-based, work di semua device) */}
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => openInAppFilePreview({ url: pdfUrl, title: 'Lampiran PDF' })}
                        disabled={!pdfUrl || loading}
                        className="d-flex align-items-center"
                      >
                        <span className="me-1">👁</span>
                        <span>Preview</span>
                      </Button>

                      {/* Tombol Download */}
                      <Button
                        color="success"
                        size="sm"
                        onClick={downloadPdf}
                        disabled={!pdfUrl || loading}
                        className="d-flex align-items-center"
                      >
                        <span className="me-1">📥</span>
                        <span>Unduh Lampiran</span>
                      </Button>
                    </div>
                  </div>

                  {loading && (
                    <div className="d-flex align-items-center">
                      <Spinner size="sm" color="primary" />
                      <span className="ms-2">Memuat dokumen...</span>
                    </div>
                  )}
                  
                  {error && <Alert color="danger" className="py-1 px-2">{error}</Alert>}
                  
                  {!loading && !error && pdfUrl && (
                    <div>
                      <div style={{ 
                        height: isMobile() ? '400px' : '500px',
                        border: '1px solid #e5e5e5',
                        borderRadius: 4,
                        overflow: 'auto'
                      }}>
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
                          <Viewer fileUrl={pdfUrl} />
                        </Worker>
                      </div>
                    </div>
                  )}
                  
                  {!loading && !error && !pdfUrl && (
                    <div className="text-muted small">
                      <div>{item.file}</div>
                      <Button 
                        color="primary" 
                        size="sm" 
                        onClick={() => {
                          // Alternatif: langsung buka file dari path
                          const fileUrl = `${process.env.REACT_APP_URL}/${item.file}`;
                          openInAppFilePreview({ url: fileUrl });
                        }}
                        className="mt-2"
                      >
                        Buka File
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        ) : null}
      </ModalBody>
    </Modal>
  );
};

export default ViewModal;